import { Suspense, lazy, useEffect, type ComponentType } from "react";
import { Navigate, Route, Routes } from "react-router-dom";

import Layout from "@/components/Layout";
import LoadingState from "@/components/LoadingState";
import RequireAuth from "@/components/RequireAuth";
import RequireRole from "@/components/RequireRole";

// Route-level code splitting: each page loads on first visit instead of
// shipping one eager bundle. This matters here because maplibre-gl is heavy
// and only some pages use it — login, legal, and portal-form visitors
// shouldn't download the mapping stack (or the timekeeping stack) at all.
//
// A dynamic import that fails usually means the tab predates the latest
// deploy: chunk filenames are content-hashed and nginx 404s the old ones.
// Reload once (guarded so a genuinely broken chunk can't loop) to pick up
// the new index.html and its chunk map.
function lazyPage(loader: () => Promise<{ default: ComponentType }>) {
  return lazy(() =>
    loader().catch((err) => {
      const KEY = "chunk-reload-at";
      const last = Number(sessionStorage.getItem(KEY) ?? 0);
      if (Date.now() - last > 10_000) {
        sessionStorage.setItem(KEY, String(Date.now()));
        window.location.reload();
        // Keep Suspense pending while the reload happens.
        return new Promise<{ default: ComponentType }>(() => {});
      }
      throw err;
    }),
  );
}

// The field-app walkthrough lives on the marketing site (one copy to
// maintain); this keeps portal.cvcarbon.eco/guide working as an address.
function GuideRedirect() {
  useEffect(() => {
    window.location.replace("https://cvcarbon.eco/guide");
  }, []);
  return <LoadingState />;
}

const LoginPage = lazyPage(() => import("@/pages/LoginPage"));
const SignupPage = lazyPage(() => import("@/pages/buyer/SignupPage"));
const BuyPage = lazyPage(() => import("@/pages/buyer/BuyPage"));
const OrdersPage = lazyPage(() => import("@/pages/buyer/OrdersPage"));
const OrderDetailPage = lazyPage(() => import("@/pages/buyer/OrderDetailPage"));
const OrderReceiptPage = lazyPage(() => import("@/pages/buyer/OrderReceiptPage"));
const PortfolioPage = lazyPage(() => import("@/pages/buyer/PortfolioPage"));
const RetirementCertificatePage = lazyPage(() => import("@/pages/buyer/RetirementCertificatePage"));
const EvidencePage = lazyPage(() => import("@/pages/buyer/EvidencePage"));
const ResetPasswordPage = lazyPage(() => import("@/pages/ResetPasswordPage"));
const PublicCreditDemoPage = lazyPage(() => import("@/pages/creditdemo/PublicCreditDemoPage"));
const PublicRetirementCertificatePage = lazyPage(() => import("@/pages/creditdemo/PublicRetirementCertificatePage"));
const CarbonCreditsPage = lazyPage(() => import("@/pages/CarbonCreditsPage"));
const PrivacyPolicyPage = lazyPage(() => import("@/pages/legal/PrivacyPolicyPage"));
const MarketplacePrivacyPage = lazyPage(() => import("@/pages/legal/MarketplacePrivacyPage"));
const SupportPage = lazyPage(() => import("@/pages/legal/SupportPage"));
const FileSharePage = lazyPage(() => import("@/pages/share/FileSharePage"));
const FileShareDownloadPage = lazyPage(() => import("@/pages/share/FileShareDownloadPage"));
const FileShareAdminPage = lazyPage(() => import("@/pages/share/FileShareAdminPage"));
const BeddingPage = lazyPage(() => import("@/pages/bedding/BeddingPage"));
const BeddingRequestPage = lazyPage(() => import("@/pages/bedding/BeddingRequestPage"));
const CheckDeliveriesPage = lazyPage(() => import("@/pages/checkdeliveries/CheckDeliveriesPage"));
const LeaseTransferPage = lazyPage(() => import("@/pages/transfers/LeaseTransferPage"));
const FieldPage = lazyPage(() => import("@/pages/field/FieldPage"));
const DashboardPage = lazyPage(() => import("@/pages/DashboardPage"));
const MapPage = lazyPage(() => import("@/pages/MapPage"));
const LeasesPage = lazyPage(() => import("@/pages/LeasesPage"));
const AnalysisPage = lazyPage(() => import("@/pages/AnalysisPage"));
const EntitiesPage = lazyPage(() => import("@/pages/EntitiesPage"));
const UsersPage = lazyPage(() => import("@/pages/UsersPage"));
const FieldActivityPage = lazyPage(() => import("@/pages/FieldActivityPage"));
const SamplingPage = lazyPage(() => import("@/pages/SamplingPage"));
const VerificationPage = lazyPage(() => import("@/pages/VerificationPage"));
const VerificationResultsPage = lazyPage(() => import("@/pages/VerificationResultsPage"));
const AnnualMonitoringPage = lazyPage(() => import("@/pages/AnnualMonitoringPage"));
const MortalityReportPage = lazyPage(() => import("@/pages/MortalityReportPage"));
const TaxFormsPage = lazyPage(() => import("@/pages/w9/TaxFormsPage"));
const W9ReviewPage = lazyPage(() => import("@/pages/w9/W9ReviewPage"));
const TimekeepingPage = lazyPage(() => import("@/pages/TimekeepingPage"));
const SettingsPage = lazyPage(() => import("@/pages/SettingsPage"));

export default function App() {
  return (
    <Suspense fallback={<LoadingState />}>
      <Routes>
        {/* Public */}
        <Route path="/login" element={<LoginPage />} />
        {/* Buyer self-signup. The signup trigger assigns role='buyer' to every
            self-signup regardless of metadata — privileged roles only come
            from admin invites (app_metadata). */}
        <Route path="/signup" element={<SignupPage />} />
        {/* Recovery-link landing — needs the recovery session, handles none. */}
        <Route path="/reset-password" element={<ResetPasswordPage />} />
        {/* Public credit registry — anonymous, backed by redacted public RPCs. */}
        <Route path="/registry" element={<PublicCreditDemoPage />} />
        <Route path="/registry/serial/:serialNumber" element={<PublicCreditDemoPage />} />
        <Route path="/registry/retirement/:certificateNumber" element={<PublicRetirementCertificatePage />} />
        <Route path="/creditdemo" element={<Navigate to="/registry" replace />} />
        {/* App-store compliance pages — must stay public (both stores follow
            these URLs during review, and the mobile app links to them). */}
        <Route path="/privacy" element={<PrivacyPolicyPage />} />
        {/* Marketplace privacy is a SEPARATE public policy: /privacy is the
            app-store compliance page and must stay free of payment/marketplace
            language reviewers don't need. */}
        <Route path="/marketplace-privacy" element={<MarketplacePrivacyPage />} />
        <Route path="/support" element={<SupportPage />} />
        {/* File share — public by design. /share is the drop box for people
            without accounts; /share/d/:shareId is the page an emailed download
            link opens (the token rides in the URL fragment). */}
        <Route path="/share" element={<FileSharePage />} />
        <Route path="/share/d/:shareId" element={<FileShareDownloadPage />} />
        {/* Field-app walkthrough → marketing site (single source). */}
        <Route path="/guide" element={<GuideRedirect />} />

        {/* Field collection — full-screen, outside the desk Layout (the map
            owns the viewport). Staff-only, like mobile collection. */}
        <Route
          path="/field"
          element={
            <RequireAuth>
              <RequireRole allow={["internal", "verifier"]}>
                <FieldPage />
              </RequireRole>
            </RequireAuth>
          }
        />

        {/* Authenticated shell */}
        <Route
          element={
            <RequireAuth>
              <Layout />
            </RequireAuth>
          }
        >
          <Route index element={<Navigate to="/dashboard" replace />} />
          <Route path="/dashboard" element={<DashboardPage />} />
          <Route
            path="/map"
            element={
              <RequireRole allow={["client", "internal", "verifier"]}>
                <MapPage />
              </RequireRole>
            }
          />
          <Route
            path="/leases"
            element={
              <RequireRole allow={["client", "internal", "verifier"]}>
                <LeasesPage />
              </RequireRole>
            }
          />
          <Route path="/settings" element={<SettingsPage />} />
          <Route
            path="/file-share"
            element={
              <RequireRole allow={["internal"]}>
                <FileShareAdminPage />
              </RequireRole>
            }
          />

          {/* Buyer marketplace */}
          <Route
            path="/buy"
            element={
              <RequireRole allow={["buyer"]}>
                <BuyPage />
              </RequireRole>
            }
          />
          <Route
            path="/orders"
            element={
              <RequireRole allow={["buyer"]}>
                <OrdersPage />
              </RequireRole>
            }
          />
          <Route
            path="/portfolio"
            element={
              <RequireRole allow={["buyer"]}>
                <PortfolioPage />
              </RequireRole>
            }
          />
          <Route
            path="/orders/:orderId"
            element={
              <RequireRole allow={["buyer"]}>
                <OrderDetailPage />
              </RequireRole>
            }
          />
          <Route
            path="/orders/:orderId/receipt"
            element={
              <RequireRole allow={["buyer"]}>
                <OrderReceiptPage />
              </RequireRole>
            }
          />
          <Route
            path="/evidence"
            element={
              <RequireRole allow={["buyer", "internal"]}>
                <EvidencePage />
              </RequireRole>
            }
          />
          <Route
            path="/retirements/:retirementId"
            element={
              <RequireRole allow={["buyer"]}>
                <RetirementCertificatePage />
              </RequireRole>
            }
          />
          <Route
            path="/credits"
            element={
              <RequireRole allow={["internal"]}>
                <CarbonCreditsPage />
              </RequireRole>
            }
          />

          {/* Staff only */}
          <Route
            path="/entities"
            element={
              <RequireRole allow={["internal"]}>
                <EntitiesPage />
              </RequireRole>
            }
          />
          <Route
            path="/users"
            element={
              <RequireRole allow={["internal"]}>
                <UsersPage />
              </RequireRole>
            }
          />
          <Route
            path="/field-activity"
            element={
              <RequireRole allow={["internal"]}>
                <FieldActivityPage />
              </RequireRole>
            }
          />
          <Route
            path="/check-deliveries"
            element={
              <RequireRole allow={["internal"]}>
                <CheckDeliveriesPage />
              </RequireRole>
            }
          />

          {/* Staff dashboards (sampling + verification cross-filtered views) */}
          <Route
            path="/analysis"
            element={
              <RequireRole allow={["internal"]}>
                <AnalysisPage />
              </RequireRole>
            }
          />
          <Route
            path="/sampling"
            element={
              <RequireRole allow={["internal", "verifier"]}>
                <SamplingPage />
              </RequireRole>
            }
          />
          <Route
            path="/verification"
            element={
              <RequireRole allow={["internal", "verifier"]}>
                <VerificationPage />
              </RequireRole>
            }
          />
          <Route
            path="/submittals"
            element={
              <RequireRole allow={["internal", "verifier"]}>
                <VerificationResultsPage />
              </RequireRole>
            }
          />
          <Route path="/verification/results" element={<Navigate to="/submittals" replace />} />

          {/* Bedding reimbursement (staff workflow: intake → match → paid) */}
          <Route
            path="/bedding"
            element={
              <RequireRole allow={["internal"]}>
                <BeddingPage />
              </RequireRole>
            }
          />

          <Route
            path="/w9-review"
            element={
              <RequireRole allow={["internal"]}>
                <W9ReviewPage />
              </RequireRole>
            }
          />

          {/* Timekeeping & expenses (internal staff; approver tab gated in-page) */}
          <Route
            path="/timekeeping"
            element={
              <RequireRole allow={["internal"]}>
                <TimekeepingPage />
              </RequireRole>
            }
          />

          {/* Client portal forms */}
          <Route
            path="/tax-forms"
            element={
              <RequireRole allow={["client"]}>
                <TaxFormsPage />
              </RequireRole>
            }
          />
          <Route
            path="/annual-monitoring"
            element={
              <RequireRole allow={["client", "internal"]}>
                <AnnualMonitoringPage />
              </RequireRole>
            }
          />
          <Route
            path="/mortality-report"
            element={
              <RequireRole allow={["client", "internal"]}>
                <MortalityReportPage />
              </RequireRole>
            }
          />
          {/* Client intake for the staff /bedding workflow. Client-only:
              staff file receipts directly from /bedding. */}
          <Route
            path="/bedding-request"
            element={
              <RequireRole allow={["client"]}>
                <BeddingRequestPage />
              </RequireRole>
            }
          />
          {/* Client notice that lease rights changed hands; staff are
              notified by email and review out-of-band. */}
          <Route
            path="/lease-transfer"
            element={
              <RequireRole allow={["client"]}>
                <LeaseTransferPage />
              </RequireRole>
            }
          />
        </Route>

        {/* 404 → dashboard */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Routes>
    </Suspense>
  );
}
